import {
  Descendant,
  EditorDraft,
  EditorVersion,
  GenericFaxArgs,
  PatientConsentFormStatus,
  PatientCoverageId,
  PopulatedNode,
  PopulatedUser,
  Template,
  TemplateId,
  TemplateKey,
  UpdateCallback,
} from '@hb/shared'
import {
  createContext,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { emptyUseDocument } from '../../../backend/db/constants'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import { UseDocument } from '../../../backend/db/types'
import { useTemplateCollectionPath } from '../hooks/useTemplates'

interface TemplateViewContextValue {
  onSave:
    | ((
        text: Descendant[],
        editorVersion: EditorVersion,
        templateId: TemplateId | null,
      ) => Promise<UpdateCallback>)
    | undefined
  onSubmit:
    | ((
        text: PopulatedNode[],
        editorVersion: EditorVersion,
        templateId: TemplateId | null,
        name: string,
        patientCoverageId: PatientCoverageId | null,
      ) => Promise<UpdateCallback>)
    | undefined
  user: PopulatedUser | null
  onFax: ((args: GenericFaxArgs) => Promise<UpdateCallback>) | undefined
  initialText?: Descendant[]
  editorVersion: EditorVersion
  template: UseDocument<Template>
  type: TemplateKey
  updateInitialText?: (version: EditorVersion, text: Descendant[]) => void
  manualTemplateId: TemplateId | null
  templateId: TemplateId | null
  setManualTemplateId: (updated: TemplateId | null) => void
  readOnly: boolean
  existingTemplateConsentFormStatus: PatientConsentFormStatus | null
}

const TemplateViewContext = createContext<TemplateViewContextValue>({
  onSave: () => {
    throw new Error('Outside of template view context')
  },
  onSubmit: () => {
    throw new Error('Outside of template view context')
  },
  onFax: () => {
    throw new Error('Outside of template view context')
  },
  manualTemplateId: null,
  templateId: null,
  user: null,
  template: emptyUseDocument,
  type: 'assessments',
  editorVersion: 'v2',
  readOnly: false,
  setManualTemplateId: () => {
    throw new Error('Outside of template view context')
  },
  existingTemplateConsentFormStatus: null,
})

export interface TemplateViewProviderProps {
  type: TemplateKey
  onSave?: TemplateViewContextValue['onSave']
  onSubmit?: TemplateViewContextValue['onSubmit']
  onFax?: TemplateViewContextValue['onFax']
  autoTemplateId?: TemplateId | null
  readOnly?: boolean
  draft?: EditorDraft | null
  user: PopulatedUser | null
}

export interface TemplateViewRef {
  draft: EditorDraft | null
  updateDraft: (draft: EditorDraft) => void
  manualTemplateId: TemplateId | null
  updateManualTemplateId: (id: TemplateId | null) => void
}

export const TemplateViewProvider = forwardRef<
  TemplateViewRef,
  PropsWithChildren<TemplateViewProviderProps>
>(
  (
    { children, onSave, onSubmit, autoTemplateId, draft: propDraft, type, onFax, readOnly, user },
    ref,
  ) => {
    const [manualTemplateId, setManualTemplateId] = useState<TemplateId | null>(null)

    const templateId = useMemo(
      () => manualTemplateId ?? autoTemplateId ?? null,
      [autoTemplateId, manualTemplateId],
    )

    const existingTemplateConsentFormStatus = useMemo(() => {
      if (!templateId) return null
      if (!templateId || type !== 'consentForm') return null
      const templateStatus = user?.consentFormsStatus?.[templateId.templateId]
      if (!templateStatus) return null
      return templateStatus
    }, [templateId, type, user])

    const collectionPath = useTemplateCollectionPath(type, templateId)
    const templateData = useDocument<Template>(collectionPath, templateId?.templateId ?? null)

    const { data: template } = templateData

    const [draft, setDraft] = useState<EditorDraft | null>(propDraft ?? null)

    const updateDraft = useCallback((draft: EditorDraft | null) => {
      setDraft(draft)
      setManualTemplateId(null)
    }, [])
    const updateManualTemplateId = useCallback((updated: TemplateId | null) => {
      setDraft(null)
      setManualTemplateId(updated ?? null)
    }, [])

    const editorVersion = useMemo<EditorVersion>(() => {
      if (manualTemplateId) return template?.editorVersion || 'v1'
      if (draft) return draft.editorVersion || 'v1'
      if (template) return template?.editorVersion || 'v1'
      return 'v2'
    }, [draft, manualTemplateId, template])

    const initialText = useMemo(() => {
      if (manualTemplateId)
        return template?.templateText && Array.isArray(template.templateText)
          ? template.templateText
          : []
      if (draft) return draft.text
      return template?.templateText ?? undefined
    }, [draft, manualTemplateId, template])

    useImperativeHandle(
      ref,
      () => ({ draft, updateDraft, manualTemplateId, updateManualTemplateId }),
      [manualTemplateId, draft, updateDraft, updateManualTemplateId],
    )

    const contextValue = useMemo<TemplateViewContextValue>(
      () => ({
        onSave,
        onSubmit,
        readOnly: !!readOnly,
        onFax,
        editorVersion,
        type,
        initialText,
        template: templateData,
        manualTemplateId,
        setManualTemplateId: updateManualTemplateId,
        templateId,
        user,
        existingTemplateConsentFormStatus,
      }),
      [
        type,
        readOnly,
        onSave,
        onSubmit,
        onFax,
        initialText,
        editorVersion,
        templateData,
        manualTemplateId,
        updateManualTemplateId,
        templateId,
        user,
        existingTemplateConsentFormStatus,
      ],
    )
    return (
      <TemplateViewContext.Provider value={contextValue}>{children}</TemplateViewContext.Provider>
    )
  },
)

export const useTemplateView = () => useContext(TemplateViewContext)
